import * as THREE from "three";
import {clamp} from "three/src/math/MathUtils";
import * as TWEEN from "@tweenjs/tween.js";
import {WizSpritePrefix, EDITORMODE} from "utils/constants";
import parser from "./parser";
import {PlayControls} from "components/Canvas/Graphics/Libs/PlayControls";

class ScriptApi {
  constructor(graphics) {
    this.ctx = graphics;

    this._duration = 0;
    this._vector3 = new THREE.Vector3();
    this._quaternion = new THREE.Quaternion();
    this._xAxis = new THREE.Vector3(1, 0, 0);
    this._yAxis = new THREE.Vector3(0, 1, 0);
    this._zAxis = new THREE.Vector3(0, 0, 1);
    this._box = new THREE.Box3();

    this.signalHandlers = {};
    this.onReduxMessageHandler = null;

    this.timer = {
      state: "off",
      timeStarted: 0,
      timePaused: 0,
      currentTime: 0,
    };
  }

  async init() {
    // scenePlay
    this.setApis(this.ctx.scenePlay);
    this.ctx.scenePlay.title = "scenePlay";
    this.ctx.scenePlay.traverse(obj => {
      if (obj.userData.canScript) {
        obj.userData.playInfo = {isAlive: true};
        obj.traverse(child => {
          child.userData.playInfo = {isAlive: true};
        });
        this.setApis(obj);
      }
    });
    this.ctx.avatar.model.title = "player";
    this.ctx.avatar.model.userData = {
      canScript: true,
      playInfo: {isAlive: true},
    };
    this.setApis(this.ctx.avatar.model);

    // sceneGUI
    this.ctx.sceneGUI.traverse(obj => {
      if (obj.userData.canScript) {
        obj.title = obj.userData.title || `${obj.name}(${obj.uuid.substring(0, 3)})`;
        obj.userData.playInfo = {isAlive: true};
        this.setApis(obj);
      }
    });

    // camera
    this.ctx.scenePlayCameras.forEach(camera => {
      this.setCameraProps(camera);
    });
  }

  getPlayer() {
    return this.getApis(this.ctx.avatar.model);
  }

  findObject(targetObj, title) {
    if (targetObj.title === title && targetObj.userData.canScript === true) return targetObj;

    for (let i = 0; i < targetObj.children.length; i++) {
      const child = targetObj.children[i];
      const object = this.findObject(child, title);
      if (object !== undefined) return object;
    }

    return undefined;
  }

  async generateUserScript() {
    const rootElement = document.querySelector("body");
    const scriptElement = document.createElement("script");
    rootElement.appendChild(scriptElement);
    scriptElement.async = true;
    scriptElement.id = "user-defined-function";

    let userScript = `
      userDefinedFunction = {};
    `;
    userScript += `
      userDefinedFunction['default'] = {};
    `;
    userScript += `
      userDefinedFunction['default']['0'] =
        async function(${WizSpritePrefix}) {
          try {
            ${parser.parse(this.ctx.scenePlay.userData.scriptJS, EDITORMODE.JAVASCRIPT_3D)}
            if (typeof Setup === "function") {
              window.SetupFunction = Setup;
            }
            if (typeof OnJoinPlayer === "function") {
              window.OnJoinPlayerFunction = OnJoinPlayer;
            }
          } catch(error) {
            throw error;
          }
        }
    `;
    userScript += `
      window.userDefinedFunction = userDefinedFunction;
    `;

    scriptElement.text = userScript;
  }

  async runUserScript() {
    const userDefinedFunction = window.userDefinedFunction;
    try {
      await userDefinedFunction["default"]["0"](this.getApis(this.ctx.scenePlay));
    } catch (error) {
      throw error;
    }
  }

  throwScriptError(error, message = null) {
    const customError = error;
    customError.name = "ScriptError";
    customError.message = message ? message : error.message;
    return customError;
  }

  publishToServer(title, api, params, eventTag) {
    const message = {title, api, params, eventTag};
    this.ctx.gameServer.room.send("api", message);
  }

  parseApiOptions(options, defaultSyncAll) {
    let syncAll = defaultSyncAll;
    let eventTag = undefined;
    if (options) {
      if (options.syncAll !== undefined) syncAll = options.syncAll;
      if (options.eventTag !== undefined) eventTag = options.eventTag;
    }
    return {syncAll, eventTag};
  }

  receiveReduxMessage(message) {
    if (this.onReduxMessageHandler) {
      this.onReduxMessageHandler(message);
    }
  }

  update() {}

  dispose() {
    const rootElement = document.querySelector("body");
    const scriptElement = document.querySelector("#user-defined-function");
    if (scriptElement !== null) rootElement.removeChild(scriptElement);

    this.signalHandlers = {};
    this.onReduxMessageHandler = null;

    this.timer = {
      state: "off",
      timeStarted: 0,
      timePaused: 0,
      currentTime: 0,
    };

    this.ctx.sceneGUI.traverse(sprite => (sprite.onClickSprite = undefined));
  }

  getApis(object) {
    const apis = {};
    if (object.type === "Scene") {
      Object.defineProperties(apis, {
        getObject: {value: object.getObject},
        getObjectsByName: {value: object.getObjectsByName},
        onSecond: {value: object.onSecond},
        wait: {value: object.wait},
        sendSignal: {value: object.sendSignal},
        onSignal: {value: object.onSignal},
        onJoin: {value: object.onJoin},
        onLeave: {value: object.onLeave},
        onKeyDown: {value: object.onKeyDown},
        onKeyUp: {value: object.onKeyUp},
        onClickEmpty: {value: object.onClickEmpty},
        startTimer: {value: object.startTimer},
        pauseTimer: {value: object.pauseTimer},
        resumeTimer: {value: object.resumeTimer},
        resetTimer: {value: object.resetTimer},
        getTimer: {value: object.getTimer},
        changeScene: {value: object.changeScene},
        stopAllSound: {value: object.stopAllSound},
        getMousePosition: {value: object.getMousePosition},
        enableKeyControl: {value: object.enableKeyControl},
        enableJoystickControl: {value: object.enableJoystickControl},
        enableAvatarControl: {value: object.enableAvatarControl},
        hideAllGui: {value: object.hideAllGui},
        getPlayerCount: {value: object.getPlayerCount},
        sendGameEvent: {value: object.sendGameEvent},
        onReduxMessage: {value: object.onReduxMessage},
        abs: {value: object.abs},
        round: {value: object.round},
        ceil: {value: object.ceil},
        floor: {value: object.floor},
        pow: {value: object.pow},
        sqrt: {value: object.sqrt},
        log: {value: object.log},
        getRandom: {value: object.getRandom},
        getYear: {value: object.getYear},
        getMonth: {value: object.getMonth},
        getDay: {value: object.getDay},
        getHour: {value: object.getHour},
        getMinutes: {value: object.getMinutes},
        getSeconds: {value: object.getSeconds},
        isKeyDown: {value: object.isKeyDown},
        isMouseDown: {value: object.isMouseDown},
        isMouseDownObject: {value: object.isMouseDownObject},
        changeBackground: {value: object.changeBackground},
        changeBackgroundColor: {value: object.changeBackgroundColor},
        setCameraProperty: {value: object.setCameraProperty},
        setZoomRange: {value: object.setZoomRange},
        cameraToggle: {value: object.cameraToggle},
        playFocus: {value: object.playFocus},
        fadeObstacle: {value: object.fadeObstacle},
      });
    } else if (object.type === "Sprite") {
      Object.defineProperties(apis, {
        setText: {value: object.setText},
        setTextSize: {value: object.setTextSize},
        show: {value: object.show},
        showAd: {value: object.showAd},
        hide: {value: object.hide},
        setOpacity: {value: object.setOpacity},
        hideAd: {value: object.hideAd},
        onClick: {value: object.onClick},
        onClickAdBanner: {value: object.onClickAdBanner},
        setPosition: {value: object.setPosition},
        getPosition: {value: object.getPosition},
        setScale: {value: object.setScale},
        move: {value: object.move},
      });
    } else {
      Object.defineProperties(apis, {
        title: {value: object.getTitle()},
        setShadow: {value: object.setShadow},
        takeShadow: {value: object.takeShadow},
        findFirstAncestor: {value: object.findFirstAncestor},
        findFirstChild: {value: object.findFirstChild},
        setMovable: {value: object.setMovable},
        applyForce: {value: object.applyForce},
        setTextureOffset: {value: object.setTextureOffset},
        setLayer: {value: object.setLayer},
        setTag: {value: object.setTag},
        removeTag: {value: object.removeTag},
        goTo: {value: object.goTo},
        goX: {value: object.goX},
        goY: {value: object.goY},
        goZ: {value: object.goZ},
        moveX: {value: object.moveX},
        moveY: {value: object.moveY},
        moveZ: {value: object.moveZ},
        getPosition: {value: object.getPosition},
        rotateX: {value: object.rotateX},
        rotateY: {value: object.rotateY},
        rotateZ: {value: object.rotateZ},
        turnX: {value: object.turnX},
        turnY: {value: object.turnY},
        turnZ: {value: object.turnZ},
        setRotation: {value: object.setRotation},
        getRotation: {value: object.getRotation},
        faceTo: {value: object.faceTo},
        onCollide: {value: object.onCollide},
        onCollideOnce: {value: object.onCollideOnce},
        onContact: {value: object.onContact},
        onCollideEnd: {value: object.onCollideEnd},
        onCollideTag: {value: object.onCollideTag},
        onClick: {value: object.onClick},
        kill: {value: object.kill},
        revive: {value: object.revive},
        playAudio: {value: object.playAudio},
        pauseAudio: {value: object.pauseAudio},
        stopAudio: {value: object.stopAudio},
        setVolume: {value: object.setVolume},
        setPlaybackRate: {value: object.setPlaybackRate},
        getVolume: {value: object.getVolume},
        playVideo: {value: object.playVideo},
        pauseVideo: {value: object.pauseVideo},
        emit: {value: object.emit},
        stopEmit: {value: object.stopEmit},
      });
      if (object === this.ctx.avatar.model) {
        Object.defineProperties(apis, {
          id: {value: object.getId()},
          spawn: {value: object.spawn},
          playAnimation: {value: object.playAnimation},
          changePlayerSpeed: {value: object.changePlayerSpeed},
          changeAxisSpeed: {value: object.changeAxisSpeed},
          changeAvatar: {value: object.changeAvatar},
          setLayer: {value: object.setLayer},
          switchCollisionTest: {value: object.switchCollisionTest},
        });
      }
      if (object.isCamera) {
        Object.defineProperties(apis, {
          enabled: {
            get() {
              return object.enabled;
            },
            set(value) {
              object.enabled = value;
            },
          },
          depth: {
            get() {
              return object.depth;
            },
            set(value) {
              object.depth = value;
            },
          },
        });
      }
    }
    return apis;
  }

  setApis(object) {
    if (object.type === "Scene") {
      this.setGeneralApis(object);
      this.setNetworkApis(object);
      this.setTimerApis(object);
      this.setOperationApis(object);
      this.setDateApis(object);
      this.setDetectionApis(object);
      this.setBackgroundApis(object);
      this.setCameraApis(object);
    } else if (object.type === "Sprite") {
      this.setGuiApis(object);
    } else {
      this.setPropertyApis(object);
      this.setTranslationApis(object);
      this.setRotationApis(object);
      this.setEventApis(object);
      this.setControlApis(object);
      this.setAudioApis(object);
      this.setVideoApis(object);
      this.setParticleSystemApis(object);
    }
    if (object === this.ctx.avatar.model) {
      this.setCharacterApis(object);
    }
  }

  setPropertyApis(object) {
    object.getTitle = () => {
      return object.title;
    };
    object.setShadow = (boolean, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "setShadow", {boolean}, eventTag);
      } else {
        object.traverse(child => {
          child.castShadow = boolean;
        });
      }
    };
    object.takeShadow = (boolean, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "takeShadow", {boolean}, eventTag);
      } else {
        object.traverse(child => {
          child.receiveShadow = boolean;
        });
      }
    };
    object.findFirstAncestor = () => {
      return object.parent ? this.getApis(object.parent) : undefined;
    };
    object.findFirstChild = () => {
      return object.children && object.children.length ? this.getApis(object.children[0]) : undefined;
    };
    object.setMovable = (boolean, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "setMovable", {boolean}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (boolean) {
          object.body.setCollisionFlags(0);
        } else {
          object.body.setCollisionFlags(2);
        }
      }
    };
    object.applyForce = (x, y, z, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "applyForce", {x, y, z}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        object.body.applyForce(x, y, z);
      }
    };
    object.setTextureOffset = (x, y, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "setTextureOffset", {x, y}, eventTag);
      } else {
        if (object.material.map) {
          object.material.map.offset.x = x;
          object.material.map.offset.y = y;
        }
      }
    };
    object.setLayer = (layer, options) => {
      const {syncAll} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.room) {
        return;
      } else {
        object.traverse(child => child.layers.set(layer));
      }
    };
    object.setTag = tag => {
      object.tag = tag;
    };
    object.removeTag = () => {
      object.tag = undefined;
    };
  }

  setGeneralApis(scene) {
    scene.getObject = title => {
      try {
        let subject = this.findObject(this.ctx.scenePlay, title) || this.findObject(this.ctx.sceneGUI, title);
        return this.getApis(subject);
      } catch (error) {
        throw this.throwScriptError(error, `${title} is not exist or canScript property is false`);
      }
    };
    scene.getObjectsByName = name => {
      let subjects = [];
      this.ctx.scenePlay.traverse(child => {
        if (child.name === name && child.title && child.userData.canScript) {
          subjects = [...subjects, this.getApis(child)];
        }
      });
      return subjects;
    };
    scene.onSecond = (sec, callback) => {
      const tween = new TWEEN.Tween()
        .to({}, sec * 1000)
        .repeat(Infinity)
        .onStart(() => callback())
        .onRepeat(() => callback())
        .start();
      return new TweenController(tween);
    };
    scene.wait = sec => {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve();
        }, sec * 1000);
      });
    };
    scene.onKeyDown = (code, callback) => {
      this.ctx.keyDownCallbacks[code] = callback;
    };
    scene.onKeyUp = (code, callback) => {
      this.ctx.keyUpCallbacks[code] = callback;
    };
    scene.onClickEmpty = callback => {
      this.ctx.onClickEmpty = callback;
    };
    scene.changeScene = (pId, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, false);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(scene.title, "changeScene", {pId}, eventTag);
      } else {
        window.location.href = `${window.location.protocol}//${window.location.host}/studio/?mode=play&pid=${pId}`;
      }
    };
    scene.stopAllSound = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(scene.title, "stopAllSound", {}, eventTag);
      } else {
        scene.traverse(child => {
          if (child.type === "Audio") {
            if (child.isPlaying === true) child.stop();
          }
        });
      }
    };
    scene.getMousePosition = () => {
      const {x, y} = this.ctx.cursorPosition;
      return {x, y};
    };
    scene.enableKeyControl = boolean => {
      if (boolean) {
        document.addEventListener("keydown", this.ctx.playKeyDownHandler);
        document.addEventListener("keyup", this.ctx.playKeyUpHandler);
      } else {
        document.removeEventListener("keydown", this.ctx.playKeyDownHandler);
        document.removeEventListener("keyup", this.ctx.playKeyUpHandler);
        this.ctx.moveDirection = {
          left: 0,
          right: 0,
          forward: 0,
          back: 0,
          jump: 0,
        };
      }
    };
    scene.enableJoystickControl = boolean => {
      if (typeof boolean === "boolean") {
        this.ctx.joystickEnabled = boolean;
      }
    };
    scene.enableAvatarControl = boolean => {
      if (boolean) {
        this.ctx.isAvatarControllerActive = true;
      } else {
        this.ctx.isAvatarControllerActive = false;
        this.ctx.moveDirection = {
          left: 0,
          right: 0,
          forward: 0,
          back: 0,
          jump: 0,
        };
      }
    };
    scene.hideAllGui = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(scene.title, "hideAllGui", {}, eventTag);
      } else {
        this.ctx.objects2D.forEach(object => {
          if (object.visible !== undefined) {
            object.visible = false;
          }
        });
      }
    };
    scene.getPlayerCount = () => {
      if (this.ctx.gameServer.room) {
        return Object.keys(this.ctx.players).length + 1;
      } else {
        return 1;
      }
    };
    scene.clearAllText = options => {};
    scene.onReduxMessage = callback => {
      this.onReduxMessageHandler = callback;
    };
    scene.sendGameEvent = (type, value) => {
      this.ctx.dispatchFromGameEvent(type, value);
    };
  }

  setNetworkApis(scene) {
    scene.sendSignal = (signal, params = {}, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(scene.title, "sendSignal", {signal, params}, eventTag);
      } else {
        if (this.signalHandlers[signal]) {
          this.signalHandlers[signal](params);
        }
      }
    };
    scene.onSignal = (signal, callback) => {
      this.signalHandlers[signal] = callback;
    };
    scene.onJoin = callback => {
      this.onJoinCallback = callback;
    };
    scene.onLeave = callback => {
      this.onLeaveCallback = callback;
    };
  }

  setTimerApis(scene) {
    // TODO: Timer apis need to be updated
    scene.startTimer = () => {
      if (this.timer.state !== "off") return;
      this.timer.state = "on";
      this.timer.timeStarted = performance.now();
    };
    scene.pauseTimer = () => {
      if (this.timer.state !== "on") return;
      this.timer.state = "paused";
      this.timer.timePaused = performance.now();
      this.timer.currentTime = (this.timer.timePaused - this.timer.timeStarted) / 1000;
    };
    scene.resumeTimer = () => {
      if (this.timer.state !== "paused") return;
      this.timer.state = "on";
      this.timer.timeStarted += performance.now() - this.timer.timePaused;
    };
    scene.resetTimer = () => {
      this.timer.state = "off";
      this.timer.currentTime = 0;
    };
    scene.getTimer = () => {
      if (this.timer.state === "on") {
        this.timer.currentTime = (performance.now() - this.timer.timeStarted) / 1000;
      }
      return this.timer.currentTime.toFixed(2);
    };
  }

  setOperationApis(scene) {
    scene.abs = number => {
      return Math.abs(number);
    };
    scene.round = (number, digit = 0) => {
      if (Number.isInteger(digit) && digit > -1) {
        return digit ? Math.round(number * Math.pow(10, digit)) / Math.pow(10, digit) : Math.round(number);
      }
    };
    scene.ceil = (number, digit = 0) => {
      if (Number.isInteger(digit) && digit > -1) {
        return digit ? Math.ceil(number * Math.pow(10, digit)) / Math.pow(10, digit) : Math.ceil(number);
      }
    };
    scene.floor = (number, digit = 0) => {
      if (Number.isInteger(digit) && digit > -1) {
        return digit ? Math.floor(number * Math.pow(10, digit)) / Math.pow(10, digit) : Math.floor(number);
      }
    };
    scene.pow = (number, exponent = 1) => {
      return Math.pow(number, exponent);
    };
    scene.sqrt = number => {
      return Math.sqrt(number);
    };
    scene.log = (number, base = false) => {
      return Math.log(number) / (base ? Math.log(base) : 1);
    };
    scene.getRandom = (min, max) => {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    };
  }

  setDateApis(scene) {
    scene.getYear = () => {
      const date = new Date();
      return date.getFullYear();
    };
    scene.getMonth = () => {
      const date = new Date();
      return date.getMonth();
    };
    scene.getDay = () => {
      const date = new Date();
      return date.getDate();
    };
    scene.getHour = () => {
      const date = new Date();
      return date.getHours();
    };
    scene.getMinutes = () => {
      const date = new Date();
      return date.getMinutes();
    };
    scene.getSeconds = () => {
      const date = new Date();
      return date.getSeconds();
    };
  }

  setDetectionApis(scene) {
    scene.isKeyDown = code => {
      return !!this.ctx.pressedKeys[code];
    };
    scene.isMouseDown = () => {
      return this.ctx.isClicked;
    };
    scene.isMouseDownObject = title => {
      if (this.ctx.isClicked) {
        let subject = this.ctx.scenePlay.getObjectByProperty("title", title);
        let intersects3D = this.ctx.getIntersects(this.ctx.cursorPosition, true);

        if (intersects3D.length > 0) {
          let object = intersects3D[0].object;

          return object === subject;
        } else return false;
      } else return false;
    };
  }

  setBackgroundApis(scene) {
    scene.changeBackground = (title, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(scene.title, "changeBackground", {title}, eventTag);
      } else {
        let skyPath = `https://d2f4dt8igm02oj.cloudfront.net/redbrick-asset/cubeTexture/${title}/${title}`;
        let sky = {
          SKY: [
            skyPath + "_px.png",
            skyPath + "_nx.png",
            skyPath + "_py.png",
            skyPath + "_ny.png",
            skyPath + "_pz.png",
            skyPath + "_nz.png",
          ],
        };

        scene.background = this.ctx.cubeTextureLoader.load(sky.SKY, () => {
          scene.background.name = `${title}`;
          scene.background.key = scene.background.uuid;
          scene.background.title = `${title}`;
          scene.background.userData = {type: "sky"};
          scene.background.encoding = THREE.sRGBEncoding;
          scene.background.needsUpdate = true;
        });
      }
    };
    scene.changeBackgroundColor = (color, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(scene.title, "changeBackgroundColor", {color}, eventTag);
      } else {
        scene.background = new THREE.Color(color);
      }
    };
  }
  setCameraApis(scene) {
    scene.setCameraProperty = (fov = 50, near = 1, far = 1000) => {
      this.ctx.playCamera.fov = fov;
      this.ctx.playCamera.near = near;
      this.ctx.playCamera.far = far;
    };
    scene.setZoomRange = (min = 10, max = 50) => {
      this.ctx.playControls.minZoom = min;
      this.ctx.playControls.maxZoom = max;
    };
    scene.cameraToggle = layer => {
      this.ctx.playCamera.layers.toggle(layer);
    };
    scene.lookAt = (x, y, z) => {
      this.ctx.playCamera.lookAt(x, y, z);
    };
    scene.setCameraPosition = (x, y, z) => {
      this.ctx.playCamera.position.set(x, y, z);
    };
    scene.togglePlayControl = () => {
      if (this.ctx.playControls) {
        this.ctx.playControls.dispose();
      } else {
        this.ctx.playControls = new PlayControls(this.ctx);
      }
    };
    scene.playFocus = (target, distance, transitionSpeed) => {
      if (this.ctx.playControls) {
        const subject = this.ctx.scenePlay.getObjectByProperty("title", target.title);
        if (subject) {
          this.ctx.playControls.playFocus(subject, distance, transitionSpeed);
        }
      }
    };
    scene.fadeObstacle = boolean => {
      this.ctx.objectObstructionOptions[0] = boolean;
    };
  }
  setGuiApis(sprite) {
    sprite.setText = (text, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "setText", {text}, eventTag);
      } else {
        sprite.getObjectByProperty("isCSS2DObject", true).element.innerText = text;
      }
    };
    sprite.setTextSize = (size, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "setTextSize", {size}, eventTag);
      } else {
        sprite.getObjectByProperty("isCSS2DObject", true).element.style.fontSize = `${size}px`;
      }
    };
    sprite.show = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "show", {}, eventTag);
      } else {
        sprite.visible = true;
      }
    };
    sprite.hide = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "hide", {}, eventTag);
      } else {
        sprite.visible = false;
      }
    };
    sprite.setOpacity = opacity => {
      sprite.material.opacity = opacity;
    };
    sprite.onClick = callback => {
      if (sprite.onClickSprite === undefined) sprite.onClickSprite = [];
      sprite.onClickSprite.push(callback.bind(sprite));
    };
    sprite.showAd = () => {
      sprite.hide();
      const size = sprite.userData.adInfo.size;
      const bool = true;
      const event = {type: "show", value: {size, bool}};
      this.ctx.adBannerEventDispatch(event);
    };
    sprite.hideAd = () => {
      sprite.hide();
      const size = sprite.userData.adInfo.size;
      const bool = false;
      const event = {type: "show", value: {size, bool}};
      this.ctx.adBannerEventDispatch(event);
    };
    sprite.onClickAdBanner = () => {
      const {click_api, click_tracking_api, landing_url} = sprite.userData.adInfo;
      fetch(click_api);
      fetch(click_tracking_api);
      window.open(landing_url, "_blank");
    };
    sprite.setPosition = (x, y, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "setPosition", {x, y}, eventTag);
      } else {
        sprite.position.set(x, y, 0);
      }
    };

    sprite.getPosition = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "getPosition", {}, eventTag);
        // TODO:
        // How to make a return object for this remote function call?
      } else {
        const {x, y} = sprite.position;
        return {x, y};
      }
    };

    sprite.setScale = (x, y, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "setScale", {x, y}, eventTag);
      } else {
        sprite.scale.set(x, y, 0);
      }
    };

    sprite.move = (dx, dy, speed, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(sprite.title, "move", {dx, dy, speed}, eventTag);
      } else {
        this._duration = (Math.sqrt(Math.abs(dx) ** 2 + Math.abs(dy) ** 2) / speed) * 1000;
        let scaleFactor = 1;
        new TWEEN.Tween(sprite.position)
          .to(
            {
              x: dx >= 0 ? `+${dx * scaleFactor}` : `${dx * scaleFactor}`,
              y: dy >= 0 ? `+${dy * scaleFactor}` : `${dy * scaleFactor}`,
            },
            this._duration,
          )
          .start();
      }
    };
  }

  setTranslationApis(object) {
    object.goTo = (x, y, z, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "goTo", {x, y, z}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        object.position.set(x, y, z);
        object.body.needUpdate = true;
      }
    };
    object.goX = (dx, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "goX", {dx}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        object.position.x += dx;
        object.body.needUpdate = true;
      }
    };
    object.goY = (dy, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "goY", {dy}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        object.position.y += dy;
        object.body.needUpdate = true;
      }
    };
    object.goZ = (dz, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "goZ", {dz}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        object.position.z += dz;
        object.body.needUpdate = true;
      }
    };
    object.moveX = (dx, speed = 1, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "moveX", {dx, speed}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        this._duration = (Math.abs(dx) / speed) * 1000;
        new TWEEN.Tween(object.position)
          .to({x: dx >= 0 ? `+${dx}` : `${dx}`}, this._duration)
          .onUpdate(() => (object.body.needUpdate = true))
          .start();
      }
    };
    object.moveY = (dy, speed = 1, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "moveY", {dy, speed}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        this._duration = (Math.abs(dy) / speed) * 1000;
        new TWEEN.Tween(object.position)
          .to({y: dy >= 0 ? `+${dy}` : `${dy}`}, this._duration)
          .onUpdate(() => (object.body.needUpdate = true))
          .start();
      }
    };
    object.moveZ = (dz, speed = 1, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "moveZ", {dz, speed}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        this._duration = (Math.abs(dz) / speed) * 1000;
        new TWEEN.Tween(object.position)
          .to({z: dz >= 0 ? `+${dz}` : `${dz}`}, this._duration)
          .onUpdate(() => (object.body.needUpdate = true))
          .start();
      }
    };
    object.getPosition = options => {
      if (object.position) {
        let {x, y, z} = object.position;
        return {x, y, z};
      }
    };
  }

  setRotationApis(object) {
    object.rotateX = (dx, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "rotateX", {dx}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        dx *= Math.PI / 180;
        object.rotateOnAxis(this._vector3.set(1, 0, 0), dx);
        object.body.needUpdate = true;
      }
    };
    object.rotateY = (dy, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "rotateY", {dy}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        dy *= Math.PI / 180;
        object.rotateOnAxis(this._vector3.set(0, 1, 0), dy);
        object.body.needUpdate = true;
      }
    };
    object.rotateZ = (dz, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "rotateZ", {dz}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        dz *= Math.PI / 180;
        object.rotateOnAxis(this._vector3.set(0, 0, 1), dz);
        object.body.needUpdate = true;
      }
    };
    object.turnX = (dx, speed = 90, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "turnX", {dx, speed}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        this._duration = (Math.abs(dx) / speed) * 1000;
        dx *= Math.PI / 180;
        const angle = {currentValue: 0, previousValue: 0};
        new TWEEN.Tween(angle)
          .to({currentValue: dx}, this._duration)
          .onUpdate(() => {
            object.quaternion.multiply(this._quaternion.setFromAxisAngle(this._xAxis, angle.currentValue - angle.previousValue));
            angle.previousValue = angle.currentValue;
            object.body.needUpdate = true;
          })
          .start();
      }
    };
    object.turnY = (dy, speed = 90, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "turnY", {dy, speed}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        this._duration = (Math.abs(dy) / speed) * 1000;
        dy *= Math.PI / 180;
        const angle = {currentValue: 0, previousValue: 0};
        new TWEEN.Tween(angle)
          .to({currentValue: dy}, this._duration)
          .onUpdate(() => {
            object.quaternion.multiply(this._quaternion.setFromAxisAngle(this._yAxis, angle.currentValue - angle.previousValue));
            angle.previousValue = angle.currentValue;
            object.body.needUpdate = true;
          })
          .start();
      }
    };
    object.turnZ = (dz, speed = 90, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "turnZ", {dz, speed}, eventTag);
      } else {
        if (!object.body || !object.userData.playInfo.isAlive) return;
        if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
        this._duration = (Math.abs(dz) / speed) * 1000;
        dz *= Math.PI / 180;
        const angle = {currentValue: 0, previousValue: 0};
        new TWEEN.Tween(angle)
          .to({currentValue: dz}, this._duration)
          .onUpdate(() => {
            object.quaternion.multiply(this._quaternion.setFromAxisAngle(this._zAxis, angle.currentValue - angle.previousValue));
            angle.previousValue = angle.currentValue;
            object.body.needUpdate = true;
          })
          .start();
      }
    };
    object.setRotation = (x, y, z) => {
      if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
      object.rotation.set(x, y, z);
      object.body.needUpdate = true;
    };
    object.getRotation = () => {
      if (object.rotation) {
        let {x, y, z} = object.rotation;
        return {x, y, z};
      }
    };
    object.faceTo = (target, yawOnly = false) => {
      if (!object.body || !object.userData.playInfo.isAlive) return;
      if (object.body.getCollisionFlags() !== 6) object.body.setCollisionFlags(2);
      if (typeof target === "object") target = target.title;
      const targetObject = this.ctx.scenePlay.getObjectByProperty("title", target);
      if (!targetObject || !targetObject.position) return;
      if (yawOnly) {
        object.lookAt(targetObject.position.x, object.position.y, targetObject.position.z);
      } else {
        object.lookAt(targetObject.position);
      }
      object.body.needUpdate = true;
    };
  }

  setEventApis(object) {
    object.onCollide = (target, callback) => {
      if (!object.body) return;
      if (typeof target === "object") target = target.title;
      const targetObject = this.ctx.scenePlay.getObjectByProperty("title", target);
      object.body.onCollision((hit, event) => {
        if (hit === targetObject && event === "start") {
          if (!object.userData.playInfo.isAlive || !hit.userData.playInfo.isAlive) return;
          callback();
        }
      });
    };
    object.onCollideOnce = (target, callback) => {
      if (!object.body) return;
      if (typeof target === "object") target = target.title;
      const targetObject = this.ctx.scenePlay.getObjectByProperty("title", target);
      object.body.onCollision((hit, event) => {
        if (hit === targetObject && event === "start") {
          if (!object.userData.playInfo.isAlive || !hit.userData.playInfo.isAlive) return;
          if (callback) callback();
          callback = false;
        }
      });
    };
    object.onContact = (target, callback) => {
      if (!object.body) return;
      if (typeof target === "object") target = target.title;
      const targetObject = this.ctx.scenePlay.getObjectByProperty("title", target);
      object.body.onCollision((hit, event) => {
        if (hit === targetObject && event === "collision") {
          if (!object.userData.playInfo.isAlive || !hit.userData.playInfo.isAlive) return;
          callback();
        }
      });
    };
    object.onCollideEnd = (target, callback) => {
      if (!object.body) return;
      if (typeof target === "object") target = target.title;
      const targetObject = this.ctx.scenePlay.getObjectByProperty("title", target);
      object.body.onCollision((hit, event) => {
        if (hit === targetObject && event === "end") {
          if (!object.userData.playInfo.isAlive || !hit.userData.playInfo.isAlive) return;
          callback();
        }
      });
    };
    object.onCollideTag = callbacks => {
      if (!object.body) return;
      object.body.onCollision((hit, event) => {
        if (event === "start" && hit.tag && callbacks[hit.tag]) {
          if (!object.userData.playInfo.isAlive || !hit.userData.playInfo.isAlive) return;
          callbacks[hit.tag](this.getApis(hit));
        }
      });
    };
    object.onClick = callback => {
      if (!object.body) return;
      if (object.onClickObject === undefined) object.onClickObject = [];
      object.onClickObject.push(callback.bind(object));
    };
  }

  setControlApis(object) {
    object.kill = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "kill", {}, eventTag);
      } else {
        if (object.userData.playInfo.isAlive === false) return;
        object.userData.playInfo.isAlive = false;
        object.userData.playInfo.layerMask = object.layers.mask;
        object.traverse(child => {
          child.userData.playInfo.isAlive = false;
          child.layers.disableAll();
          if (child.type === "Audio") child.stop();
        });
        if (object.body) {
          object.userData.playInfo.collisionFlag = object.body.getCollisionFlags();
          object.body.setCollisionFlags(5);
        }
      }
    };
    object.revive = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "revive", {}, eventTag);
      } else {
        if (object.userData.playInfo.isAlive === true) return;
        object.userData.playInfo.isAlive = true;
        object.traverse(child => {
          child.userData.playInfo.isAlive = true;
          child.layers.mask = object.userData.playInfo.layerMask;
        });
        if (object.body) {
          object.body.setCollisionFlags(object.userData.playInfo.collisionFlag);
        }
      }
    };
  }

  setAudioApis(object) {
    object.playAudio = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "playAudio", {}, eventTag);
      } else {
        const audio = object.getObjectByProperty("type", "Audio");
        if (audio && !audio.isPlaying) audio.play();
      }
    };
    object.pauseAudio = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "pauseAudio", {}, eventTag);
      } else {
        const audio = object.getObjectByProperty("type", "Audio");
        if (audio && audio.isPlaying) audio.pause();
      }
    };
    object.stopAudio = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "stopAudio", {}, eventTag);
      } else {
        const audio = object.getObjectByProperty("type", "Audio");
        if (audio && audio.isPlaying) audio.stop();
      }
    };
    object.setVolume = (volume, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "setVolume", {volume}, eventTag);
      } else {
        const audio = object.getObjectByProperty("type", "Audio");
        if (audio) audio.setVolume(clamp(volume, 0, 10));
      }
    };
    object.setPlaybackRate = (playbackRate, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "setPlaybackRate", {playbackRate}, eventTag);
      } else {
        const audio = object.getObjectByProperty("type", "Audio");
        if (audio) audio.setPlaybackRate(clamp(playbackRate, 0.1, 5));
      }
    };
    object.getVolume = () => {
      const audio = object.getObjectByProperty("type", "Audio");
      if (audio) return audio.getVolume();
    };
  }

  setVideoApis(object) {
    object.playVideo = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "playAudio", {}, eventTag);
      } else {
        if (object.userData.type === "video") {
          this.ctx.videoElements[object.uuid].play();
        }
      }
    };
    object.pauseVideo = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "pauseAudio", {}, eventTag);
      } else {
        if (object.userData.type === "video") {
          this.ctx.videoElements[object.uuid].pause();
        }
      }
    };
  }

  setParticleSystemApis(object, options) {
    object.emit = (life, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "emit", {life}, eventTag);
      } else {
        const system = this.ctx.particleSystemArray.find(system => system.emitters[0].id === object.emitterId);
        system.emitters[0].emit(life, Infinity);
      }
    };
    object.stopEmit = options => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(object.title, "stopEmit", {}, eventTag);
      } else {
        const system = this.ctx.particleSystemArray.find(system => system.emitters[0].id === object.emitterId);
        system.emitters[0].stopEmit();
        object.children = [];
      }
    };
  }

  setCharacterApis(object) {
    object.getId = () => {
      if (this.ctx.gameServer.room) {
        if (this.ctx.gameServer.room.sessionId !== undefined) {
          return this.ctx.gameServer.room.sessionId;
        } else {
          setTimeout(() => {
            object.getId();
          }, 100);
        }
      }
    };
    object.spawn = target => {
      if (!object.body) return;

      let targetObject, targetPosition;
      if (target) {
        if (target.isVector3) targetPosition = target;
        else {
          targetObject = this.ctx.scenePlay.getObjectByProperty("title", target.title);
          if (targetObject) targetPosition = targetObject.position;
        }
      } else if (this.ctx.startLocation) {
        targetObject = this.ctx.startLocation;
        targetPosition = this.ctx.startLocation.position;
      }
      if (!targetPosition) return;

      this._box.setFromObject(object);
      const offset = 1 + (this._box.max.y - this._box.min.y) / 2;
      object.body.transform();
      if (targetObject) {
        this._box.setFromObject(targetObject);
        object.body.setPosition(targetPosition.x, this._box.max.y + offset, targetPosition.z);
      } else {
        object.body.setPosition(targetPosition.x, targetPosition.y + offset, targetPosition.z);
      }
      object.body.refresh();
    };
    object.playAnimation = (mode, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(null, "playAnimation", {mode}, eventTag);
      } else {
        object.manager.playAnimation(mode);
      }
    };
    object.changePlayerSpeed = (speed, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(null, "changePlayerSpeed", {speed}, eventTag);
      } else {
        object.manager.speedFactor = speed;
      }
    };
    object.changeAxisSpeed = (x, y, z, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(null, "changePlayerSpeed", {x, y, z}, eventTag);
      } else {
        object.manager.deltaPosDefault.x = x;
        object.manager.deltaPosDefault.y = y;
        object.manager.deltaPosDefault.z = z;
      }
    };
    object.changeLook = (avatarId, options) => {
      const {syncAll, eventTag} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.gameServer.room) {
        this.publishToServer(null, "changeLook", {}, eventTag);
      } else {
        if (this.ctx.avatar) {
          this.ctx.avatar.changeLook(avatarId);
        }
      }
    };
    object.setLayer = (layer, options) => {
      const {syncAll} = this.parseApiOptions(options, true);
      if (syncAll && this.ctx.room) {
        return;
      } else {
        object.traverse(child => child.layers.set(layer));
      }
    };
    object.switchCollisionTest = () => {
      this.ctx.toggleCollisionDetection();
    };
  }

  setCameraProps(camera) {
    const {enabled, depth} = camera.userData;
    const context = this.ctx;
    Object.defineProperties(camera, {
      enabled: {value: enabled, writable: true},
      _depth: {value: depth, writable: true},
      depth: {
        get() {
          return this._depth;
        },
        set(value) {
          this._depth = value;
          context.updateCameraOrder();
        },
      },
    });
  }
}
export default ScriptApi;

class TweenController {
  constructor(tween) {
    this.start = () => tween.start();
    this.stop = () => tween.stop();
  }
}
